<script setup>
import { useDialogManager } from '@0x234/common-vue/composables/dialog-manager';
import AppFooter from 'components/AppFooter.vue';
import AppLogo from 'components/AppLogo.vue';
import LegalUpdateNotice from 'components/LegalUpdateNotice.vue';
import NavItem from 'components/NavItem.vue';
import ThemeModeToggleButton from 'components/ThemeModeToggleButton.vue';
import UserMenuToggle from 'components/UserMenuToggle.vue';
import { useQuasar } from 'quasar';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { buildAccountNavigation, buildPrimaryNavigation } from 'src/utils/product-navigation-util';
import { openComponentDialog } from 'src/utils/quasar-util';
import { useAppStore } from 'stores/app-store';
import { useAuthStore } from 'stores/auth-store';
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue';

const $q = useQuasar();
const appStore = useAppStore();
const authStore = useAuthStore();
const dialogManager = useDialogManager();

const scrollPosition = ref(0);

const topNavLinks = computed(() => {
    return buildPrimaryNavigation({ isAuthenticated: authStore.userAuthenticated });
});

const accountNavLinks = computed(() => {
    if (!authStore.userAuthenticated) {
        return [];
    }
    return buildAccountNavigation({ hasProfileLinkAccess: authStore.user?.role === 'Xprt' });
});

const sideNavLinks = computed(() => {
    return [
        ...topNavLinks.value,
        ...accountNavLinks.value,
        {
            title: 'Login',
            icon: 'sym_r_login',
            route: AppRoute.build(Route.Login),
            visible: !authStore.userAuthenticated,
            groupIndex: 2
        },
        {
            title: 'Help',
            icon: 'sym_r_help_center',
            route: () => openComponentDialog(defineAsyncComponent(() => import('components/HelpDialog.vue'))),
            visible: authStore.userAuthenticated,
            groupIndex: 3
        },
        {
            title: 'Logout',
            icon: 'sym_r_logout',
            route: () => dialogManager.openDialogComponent(defineAsyncComponent(() => import('components/UserLogoutDialog.vue'))),
            visible: authStore.userAuthenticated,
            groupIndex: 4
        }
    ].filter(link => link.visible !== false);
});

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});

// The drawer is the small-screen menu only; the top nav takes over above the medium breakpoint, so a drawer left
// open while the viewport widens would have no visible control to close it.
watch(() => $q.screen.gt.sm, isWide => {
    if (isWide) {
        appStore.drawerIsOpen = false;
    }
});

function handleScroll() {
    scrollPosition.value = window.scrollY;
}

function toggleDrawerIsOpen() {
    appStore.drawerIsOpen = !appStore.drawerIsOpen;
}

function openUserMenuDialog() {
    openComponentDialog(defineAsyncComponent(() => import('components/UserMenuDialog.vue')));
}
</script>

<template>
    <q-layout id="main-layout" class="main-layout" view="hHh lpR fFf">
        <a class="skip-link no-hover-decoration bg-primary text-white text-weight-medium q-px-md q-py-sm" href="#main-content">Skip to content</a>
        <q-header :class="[ 'main-header', { 'scrolled': scrollPosition > 40 || appStore.drawerIsOpen, 'drawer-open': appStore.drawerIsOpen } ]">
            <q-toolbar class="q-py-none">
                <div class="container-md flex">
                    <div class="side-container justify-start q-gutter-x-md q-mr-md">
                        <q-btn
                            aria-label="Menu"
                            class="drawer-toggle lt-md"
                            color="primary"
                            flat
                            icon="sym_r_menu"
                            padding="6px"
                            @click="toggleDrawerIsOpen"
                        />
                        <q-toolbar-title class="flex column justify-center items-start">
                            <AppLogo/>
                        </q-toolbar-title>
                    </div>
                    <nav aria-label="Primary navigation" class="nav-container top-nav text-center">
                        <q-list class="flex row justify-center q-gutter-x-sm">
                            <NavItem
                                v-for="link of topNavLinks"
                                :key="link.route"
                                :link="link"
                                class="text-weight-medium"
                                hide-icons
                            />
                        </q-list>
                    </nav>
                    <div class="side-container actions-container justify-end q-ml-sm q-gutter-x-sm">
                        <ThemeModeToggleButton/>
                        <UserMenuToggle
                            v-if="authStore.userAuthenticated"
                            class="cursor-pointer"
                            @click="openUserMenuDialog"
                        />
                        <q-btn
                            v-else
                            :to="AppRoute.build(Route.Login)"
                            class="gt-sm"
                            color="primary"
                            label="Log in"
                            no-caps
                            unelevated
                        />
                    </div>
                </div>
            </q-toolbar>
        </q-header>
        <q-drawer
            v-model="appStore.drawerIsOpen"
            aria-label="Main navigation"
            behavior="mobile"
            class="q-px-sm"
            overlay
            role="navigation"
            side="left"
        >
            <q-scroll-area :horizontal-thumb-style="{ opacity: 0 }" class="fit">
                <div class="overlay-header items-center">
                    <q-btn
                        aria-label="Menu"
                        class="drawer-toggle q-mr-md"
                        color="primary"
                        flat
                        icon="sym_r_menu"
                        padding="6px"
                        @click="toggleDrawerIsOpen"
                    />
                    <q-toolbar-title class="flex column justify-center items-start">
                        <AppLogo/>
                    </q-toolbar-title>
                </div>
                <q-list class="q-gutter-y-sm q-mt-sm">
                    <template v-for="(link, index) of sideNavLinks" :key="link.route || link.title">
                        <div v-if="index > 0 && link.groupIndex !== sideNavLinks[index - 1].groupIndex" class="q-my-lg"/>
                        <NavItem :link="link"/>
                    </template>
                </q-list>
            </q-scroll-area>
        </q-drawer>
        <q-page-container>
            <q-page
                :style-fn="o => ({ minHeight: o ? `calc(100vh - ${o}px)` : '100vh' })"
                id="main-content"
                class="flex column"
                tabindex="-1"
            >
                <q-no-ssr>
                    <LegalUpdateNotice/>
                </q-no-ssr>
                <router-view name="dialog"/>
                <slot v-if="$slots.default"/>
                <router-view v-else/>
                <AppFooter/>
            </q-page>
        </q-page-container>
    </q-layout>
</template>

<style lang="scss" scoped>
// Off screen until a keyboard visitor tabs to it, then in view above the header.
.skip-link {
    left: 16px;
    position: fixed;
    top: -100px;
    z-index: 3000;

    &:focus {
        top: 16px;
    }
}

.q-header {
    background: revert;
}

.q-toolbar {
    backdrop-filter: blur(20px);
    background: rgba($white, 0.95);
    border-bottom: 1px solid rgba($primary, 0.16);
    color: $default;
    height: 62px;
    justify-content: space-between;
    min-width: 320px;

    .body--dark & {
        background: rgba($dark-page, 0.95) !important;
        color: $white;
    }

    .q-toolbar__title {
        padding: 0;
    }

    .nav-container,
    .side-container {
        align-items: center;
        display: flex;
        flex: 1;

        @media(max-width: $breakpoint-sm-max) {
            &.actions-container {
                flex: 0;
            }
        }
    }

    .nav-container {
        flex: 0 1 auto;
        justify-content: center;

        @media(max-width: $breakpoint-sm-max) {
            display: none;
        }
    }
}

.overlay-header {
    display: none;

    .q-drawer--mobile & {
        display: flex;
        padding: 12px 4px;

        .q-toolbar__title {
            padding-left: 8px;
        }

        .body--dark & {
            border-bottom-color: $separator-dark-color;
        }
    }
}

.q-drawer--mobile .q-toolbar__title {
    padding: 0 !important;
}
</style>

<style lang="scss">
#main-layout {
    .q-drawer {
        body:not(.q-body--prevent-scroll) & {
            z-index: 998;
        }

        .q-item {
            background: none;
        }
    }

    .q-drawer,
    .q-toolbar {
        .app-logo {
            height: 34px;
            width: auto;
        }
    }

    .top-nav {
        .q-item {
            background: transparent;
            box-shadow: inset 0 -2px 0 transparent;
            height: 62px;
            min-height: auto;
            padding-bottom: 0;
            padding-top: 0;
            transition: box-shadow 120ms ease-in-out, color 120ms ease-in-out;

            &:hover,
            &.q-item--active,
            &.q-router-link--active:not(.highlight-exact-only),
            &.q-router-link--exact-active {
                color: var(--q-primary);
            }

            &.q-router-link--active:not(.highlight-exact-only),
            &.q-router-link--exact-active {
                box-shadow: inset 0 -2px 0 var(--q-primary);
            }

            .q-item__section {
                margin-left: 8px;
                margin-right: 8px;
            }

            .q-item__label {
                letter-spacing: 0.04em;
            }
        }
    }

    .q-drawer.q-drawer--left {
        &.q-drawer--on-top {
            backdrop-filter: blur(20px);
            background: rgba($white, 0.95);
            border-right: 1px solid rgba($primary, 0.16);
            margin-top: -1px;

            .body--dark & {
                background: rgba($dark-page, 0.95);
            }
        }

        &.q-drawer--bordered {
            border-top: 1px solid $separator-color;

            .body--dark & {
                border-top-color: $separator-dark-color;
            }
        }
    }
}
</style>
